// ─── Sélecteur de date (modale mini-calendrier) — Core ───────────
// `moisSeul` : ne choisir qu'un MOIS (AAAA-MM) — pour les champs qui n'ont
// pas de jour (début / fin d'une prévision). La vue s'ouvre sur la grille des
// douze mois, et le choix vaut validation.
// Réutilisable app-wide. <SelecteurDate visible valeur="2026-07-26"
//   onChange={(dk)=>…} onFermer={…} />. Renvoie une date AAAA-MM-JJ.
import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import Feuille from './Feuille';

const pad = (n) => String(n).padStart(2, '0');
const fmt = (a, m, j) => `${a}-${pad(m + 1)}-${pad(j)}`;
const premierCol = (a, m) => (new Date(a, m, 1).getDay() + 6) % 7;
const nbJours = (a, m) => new Date(a, m + 1, 0).getDate();

export default function SelecteurDate({
  visible, valeur, titre, onChange, onFermer, avecEffacer = false, min, moisSeul = false,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const MOIS = t('commun.moisLongs');
  const MOIS_COURT = t('commun.moisCourts');
  const JOURS = t('commun.joursCourts');
  const titreFinal = titre ?? t('commun.choisirDate');
  const auj = new Date();
  const RE = moisSeul ? /^\d{4}-\d{2}$/ : /^\d{4}-\d{2}-\d{2}$/;
  const init = RE.test(valeur || '')
    ? [...valeur.split('-').map(Number), auj.getDate()].slice(0, 3)
    : [auj.getFullYear(), auj.getMonth() + 1, auj.getDate()];
  const [an, setAn] = useState(init[0]);
  const [mo, setMo] = useState(init[1] - 1);
  const [vue, setVue] = useState('jours');   // 'jours' | 'mois' (sélection rapide mois/année)

  useEffect(() => {
    if (!visible) return;
    setVue(moisSeul ? 'mois' : 'jours');
    if (RE.test(valeur || '')) { const [y, m] = valeur.split('-').map(Number); setAn(y); setMo(m - 1); }
  }, [visible, valeur, moisSeul]);

  const cases = useMemo(() => {
    const arr = []; const deb = premierCol(an, mo); const tot = nbJours(an, mo);
    for (let i = 0; i < deb; i++) arr.push(null);
    for (let j = 1; j <= tot; j++) arr.push(j);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [an, mo]);

  const prec = () => { if (mo === 0) { setMo(11); setAn(a => a - 1); } else setMo(m => m - 1); };
  const suiv = () => { if (mo === 11) { setMo(0); setAn(a => a + 1); } else setMo(m => m + 1); };
  const estSel = (j) => valeur === fmt(an, mo, j);
  // En mode mois, la case retenue est le MOIS lui-même, pas un jour.
  const moisSel = (k) => moisSeul && valeur === `${an}-${String(k + 1).padStart(2, '0')}`;
  const estAuj = (j) => an === auj.getFullYear() && mo === auj.getMonth() && j === auj.getDate();
  const estAvantMin = (j) => /^\d{4}-\d{2}-\d{2}$/.test(min || '') && fmt(an, mo, j) < min;

  const boutons = avecEffacer ? (
    <TouchableOpacity onPress={() => { onChange?.(''); onFermer?.(); }} hitSlop={HIT} accessibilityLabel={t('commun.aucuneDate')}>
      <Text style={s.effacer}>{t('commun.aucune')}</Text>
    </TouchableOpacity>
  ) : null;

  return (
    <Feuille visible={visible} titre={titreFinal} emoji="📅" hauteur={0.8} onFermer={onFermer} boutons={boutons}>
      {vue === 'mois' ? (
        <>
          {/* Sélection rapide : navigation d'année + grille de 12 mois. */}
          <View style={s.barre}>
            <TouchableOpacity onPress={() => setAn(a => a - 1)} hitSlop={HIT}><Text style={s.nav}>‹</Text></TouchableOpacity>
            <Text style={s.mois}>{an}</Text>
            <TouchableOpacity onPress={() => setAn(a => a + 1)} hitSlop={HIT}><Text style={s.nav}>›</Text></TouchableOpacity>
          </View>
          <View style={s.grilleMois}>
            {MOIS_COURT.map((m, i) => {
              const sel = moisSeul ? moisSel(i) : i === mo;
              const estMoisAuj = i === auj.getMonth() && an === auj.getFullYear();
              return (
                <TouchableOpacity key={i} style={s.caseMoisWrap} onPress={() => {
                    // `moisSeul` : le mois EST la réponse. Descendre au
                    // calendrier ferait choisir un jour inutile — qu'on
                    // jetterait ensuite en silence.
                    if (moisSeul) {
                      onChange?.(`${an}-${String(i + 1).padStart(2, '0')}`);
                      onFermer?.(); return;
                    }
                    setMo(i); setVue('jours');
                  }}>
                  <View style={[s.cellMois, sel && s.cellSel, !sel && estMoisAuj && s.cellAuj]}>
                    <Text style={[s.cellMoisTxt, sel && s.cellTxtSel, !sel && estMoisAuj && s.cellTxtAuj]}>{m}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      ) : (
        <>
          <View style={s.barre}>
            <TouchableOpacity onPress={prec} hitSlop={HIT}><Text style={s.nav}>‹</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => setVue('mois')} hitSlop={HIT} accessibilityLabel={t('commun.choisirMoisAnnee')}>
              <Text style={s.mois}>{MOIS[mo]} {an} <Text style={s.moisChev}>▾</Text></Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={suiv} hitSlop={HIT}><Text style={s.nav}>›</Text></TouchableOpacity>
          </View>
          <View style={s.semaine}>{JOURS.map((d, i) => <Text key={i} style={s.jourEntete}>{d}</Text>)}</View>
          <View style={s.grille}>
            {cases.map((j, i) => (
              <View key={i} style={s.caseWrap}>
                {j ? (
                  <TouchableOpacity disabled={estAvantMin(j)} style={[s.cell, estSel(j) && s.cellSel, !estSel(j) && estAuj(j) && s.cellAuj, estAvantMin(j) && s.cellOff]}
                    onPress={() => { onChange?.(fmt(an, mo, j)); onFermer?.(); }}>
                    <Text style={[s.cellTxt, estSel(j) && s.cellTxtSel, !estSel(j) && estAuj(j) && s.cellTxtAuj, estAvantMin(j) && s.cellTxtOff]}>{j}</Text>
                  </TouchableOpacity>
                ) : <View style={s.cell} />}
              </View>
            ))}
          </View>
        </>
      )}
    </Feuille>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  barre: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 8, paddingVertical: 6 },
  nav: { fontSize: 26, color: UI.accent, fontWeight: '300', width: 40, textAlign: 'center' },
  mois: { fontSize: 16, fontWeight: '800', color: UI.ink },
  moisChev: { fontSize: 13, color: UI.accent, fontWeight: '800' },
  grilleMois: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 },
  caseMoisWrap: { width: `${100 / 3}%`, padding: 5 },
  cellMois: { paddingVertical: 16, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  cellMoisTxt: { fontSize: 15, fontWeight: '700', color: UI.ink },
  semaine: { flexDirection: 'row', marginTop: 6, marginBottom: 4 },
  jourEntete: { flex: 1, textAlign: 'center', fontSize: 12, fontWeight: '700', color: UI.faint },
  grille: { flexDirection: 'row', flexWrap: 'wrap' },
  caseWrap: { width: `${100 / 7}%`, aspectRatio: 1, padding: 3 },
  cell: { flex: 1, borderRadius: 999, alignItems: 'center', justifyContent: 'center' },
  cellSel: { backgroundColor: UI.accent },
  cellAuj: { borderWidth: 1.5, borderColor: UI.accent },
  cellTxt: { fontSize: 14.5, fontWeight: '600', color: UI.ink },
  cellTxtSel: { color: '#fff', fontWeight: '800' },
  cellTxtAuj: { color: UI.accent, fontWeight: '800' },
  cellOff: { opacity: 0.3 },
  cellTxtOff: { color: UI.faint },
  effacer: { fontSize: 13, color: UI.muted, fontWeight: '700' },
});
